import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { MAX_INPUT_LENGTH_2048 } from '../../../../../../constants';
import styles from './PatientAlignerBlock.module.scss';
import cx from 'classnames';
import { Button, Icon, RadioButton, RolePermissionChecker, Text, Textarea } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentUser } from 'store/auth/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import {
  AlignerTreatmentPlanText,
  AlignerTreatmentPlanUpperOrLowerType,
  TreatmentPlanTask,
  TreatmentPlanText,
  TreatmentPlanType,
  UserRole,
} from 'types';
import { onlyLatinLettersSymbolsNumbers } from 'utils';

type Props = {
  id: number;
  treatmentPlanType: TreatmentPlanType;
  alignerTreatmentPlanUpperOrLowerType: AlignerTreatmentPlanUpperOrLowerType;
  treatmentNote: string | undefined;
  task: TreatmentPlanTask;
  isBlocked: boolean;
  assignClinicID: string;
};

const schema = yup.object({
  treatmentNote: yup.string().matches(onlyLatinLettersSymbolsNumbers, {
    message: 'Only Latin Alphabet is allowed',
  }),
  treatmentPlanType: yup.string(),
  alignerTreatmentPlanUpperOrLowerType: yup.string(),
});

const allowToEditAlignerForDoctor = [
  TreatmentPlanTask.LETTER_TO_DO,
  TreatmentPlanTask.LETTER_SUBMITTED,
  TreatmentPlanTask.LETTER_SENT,
  TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
];

const allowToEditAlignerForAdmin = [
  TreatmentPlanTask.COMPLETED,
  TreatmentPlanTask.TREATMENT_PRE_FINISH,
  TreatmentPlanTask.TREATMENT_POST_FINISH,
  TreatmentPlanTask.IN_TREATMENT,
];

export const PatientAlignerBlock = ({
  id,
  treatmentPlanType,
  alignerTreatmentPlanUpperOrLowerType,
  treatmentNote,
  task,
  isBlocked,
  assignClinicID,
}: Props) => {
  const [alignerEditMode, setAlignerEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(patientsActions.setShouldPatientPageUpdate(false));
    dispatch(patientsActions.setIsPatientPageEdited(true));
    return () => {
      dispatch(patientsActions.setShouldPatientPageUpdate(true));
      dispatch(patientsActions.setIsPatientPageEdited(false));
    };
  }, [dispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      treatmentNote: treatmentNote ?? '',
      treatmentPlanType,
      alignerTreatmentPlanUpperOrLowerType,
    },
  });

  const onClose = () => {
    setAlignerEditMode(false);
  };

  const onSubmitTreatment = handleSubmit(async (formData) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      console.log('formData', formData);
      console.log(
        formData.treatmentPlanType !== TreatmentPlanText.NONE && formData.treatmentPlanType !== TreatmentPlanText.OTHER
          ? (formData.alignerTreatmentPlanUpperOrLowerType as AlignerTreatmentPlanUpperOrLowerType)
          : AlignerTreatmentPlanUpperOrLowerType.NONE,
      );
      const wrappedResult = dispatch(
        patientsActions.updatePatientAlignerPlan({
          patientCaseData: {
            treatmentNote: !disableAligner && formData.treatmentNote ? formData.treatmentNote : null,
            alignerTreatmentPlanUpperOrLowerType: !disableAligner
              ? (formData.alignerTreatmentPlanUpperOrLowerType as AlignerTreatmentPlanUpperOrLowerType)
              : AlignerTreatmentPlanUpperOrLowerType.NONE,
            treatmentPlanType: formData.treatmentPlanType as TreatmentPlanType,
          },
          patientCaseId: id,
        }),
      );
      await wrappedResult.unwrap();
      onClose();
      dispatch(appActions.setModalErrorText({ name: 'treatmentAndDiagnosisError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'treatmentAndDiagnosisError', errorText: error?.title }));
      dispatch(appActions.setIsModalOpened({ isOpened: true, name: 'treatmentAndDiagnosisError' }));
      onClose();
    } finally {
      setIsLoading(false);
    }
  });

  const currentUser = useSelector(selectCurrentUser);
  const currentUserID = currentUser?.id;

  const watchedTreatmentNote = watch('treatmentNote');
  const watchedTreatmentPlanType = watch('treatmentPlanType');

  const isTreatmentPlanAllowedToEditForDoctor = allowToEditAlignerForDoctor.includes(task);
  const isTreatmentPlanAllowedToEditForAdmin = allowToEditAlignerForAdmin.includes(task);
  const showTreatmentControls =
    (!isBlocked && isTreatmentPlanAllowedToEditForDoctor && assignClinicID === currentUserID) ||
    (currentUser?.role === UserRole.ADMIN && isTreatmentPlanAllowedToEditForAdmin);
  console.log('showTreatmentControls', showTreatmentControls);

  const disableAligner =
    watchedTreatmentPlanType === TreatmentPlanType.NONE || watchedTreatmentPlanType === TreatmentPlanType.OTHER;

  return (
    <div className={styles.wrapper}>
      <div className={styles.blockWrapper}>
        <div className={styles.treatmentWrapper}>
          <div className={styles.infoBlockWrapper}>
            <div className={styles.infoBlock}>
              <Text type="overline" className={styles.upperCaseText}>
                Aligner final treatment plan
              </Text>
              {!alignerEditMode && (
                <Text type="caption" className={treatmentPlanType ? '' : styles.textNotSet}>
                  {TreatmentPlanText[treatmentPlanType] || 'Please fill in the treatment plan type'}
                </Text>
              )}
            </div>
            <RolePermissionChecker rolesAllowedToView={[UserRole.CLINIC, UserRole.ADMIN]}>
              {!alignerEditMode && showTreatmentControls && (
                <div
                  className={cx(styles.iconWrapper, { [styles.iconWhite]: !!treatmentPlanType })}
                  onClick={() => setAlignerEditMode(true)}>
                  {<Icon name={treatmentPlanType ? 'Edit' : 'Plus'} />}
                </div>
              )}
            </RolePermissionChecker>
          </div>

          {alignerEditMode && (
            <div className={styles.radioButtonWrapper}>
              <div className={styles.radioButtonBlock}>
                <RadioButton value="LITE" id="lite" register={register} inputName="treatmentPlanType" />
                <label htmlFor="lite">Lite</label>
              </div>
              <div className={styles.radioButtonBlock}>
                <RadioButton value="MODERATE" id="moderate" register={register} inputName="treatmentPlanType" />
                <label htmlFor="moderate">Moderate</label>
              </div>
              <div className={styles.radioButtonBlock}>
                <RadioButton value="ADVANCED" id="advanced" register={register} inputName="treatmentPlanType" />
                <label htmlFor="advanced">Advanced</label>
              </div>
              <div className={styles.radioButtonBlock}>
                <RadioButton value="OTHER" id="other" register={register} inputName="treatmentPlanType" />
                <label htmlFor="other">Other</label>
              </div>
              <div className={styles.radioButtonBlock}>
                <RadioButton value="NONE" id="none" register={register} inputName="treatmentPlanType" />
                <label htmlFor="none">Not Selected</label>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.line} />
      {!alignerEditMode && (
        <Text type="caption" className={alignerTreatmentPlanUpperOrLowerType ? '' : styles.textNotSet}>
          {AlignerTreatmentPlanText[alignerTreatmentPlanUpperOrLowerType] ||
            'Please fill in aligner treatment plan type'}
        </Text>
      )}
      {alignerEditMode && (
        <div className={styles.radioButtonWrapper}>
          <div className={styles.radioButtonBlock}>
            <RadioButton
              value="UPPER"
              id="upper"
              register={register}
              inputName="alignerTreatmentPlanUpperOrLowerType"
              disabled={disableAligner}
            />
            <label htmlFor="upper" className={!disableAligner ? '' : styles.disabled}>
              Upper
            </label>
          </div>
          <div className={styles.radioButtonBlock}>
            <RadioButton
              value="LOWER"
              id="lower"
              register={register}
              inputName="alignerTreatmentPlanUpperOrLowerType"
              disabled={disableAligner}
            />
            <label htmlFor="lower" className={!disableAligner ? '' : styles.disabled}>
              Lower
            </label>
          </div>
          <div className={styles.radioButtonBlock}>
            <RadioButton
              value="UPPER_AND_LOWER"
              id="upperAndLower"
              register={register}
              inputName="alignerTreatmentPlanUpperOrLowerType"
              disabled={disableAligner}
            />
            <label htmlFor="upperAndLower" className={!disableAligner ? '' : styles.disabled}>
              Upper & Lower
            </label>
          </div>
          <div className={styles.radioButtonBlock}>
            <RadioButton
              value="NONE"
              id="none"
              register={register}
              inputName="alignerTreatmentPlanUpperOrLowerType"
              disabled={disableAligner}
            />
            <label htmlFor="none" className={!disableAligner ? '' : styles.disabled}>
              Not Selected
            </label>
          </div>
        </div>
      )}
      <div className={styles.line} />
      {!alignerEditMode && (
        <>
          <Text type="overline" className={styles.upperCaseText}>
            Treatment note
          </Text>
          <Text type="caption" className={treatmentNote ? '' : styles.textNotSet}>
            {treatmentNote || 'Please fill in the treatment note (optional)'}
          </Text>
        </>
      )}
      {alignerEditMode && (
        <Textarea
          labelText="Treatment note"
          placeholder="Please fill in the treatment note (optional)"
          inputName="treatmentNote"
          register={register}
          maxLength={MAX_INPUT_LENGTH_2048}
          autoComplete="off"
          characterCounter
          errorMsg={errors.treatmentNote?.message as string}
          currentValue={watchedTreatmentNote}
          setValue={setValue}
          isForLinks={false}
          disabled={disableAligner}
        />
      )}
      {alignerEditMode && (
        <div className={styles.buttonRow}>
          <Button onClick={onClose}>Cancel</Button>
          <Button className={styles.submitButton} onClick={onSubmitTreatment}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
};
